// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Components
import { Paragraph, ParagraphWeight } from '../../../layout/elements/Paragraph';
import { Select, SelectSizeType } from '../../../layout/elements/Select';

// Store
import {
  getLanguage,
  setLanguage,
} from '../../../store/app.store';
import { getUser } from '../../../store/session.store';

// Locale
import { Language, fallbackLanguage } from '../../../locale/i18n';

// Plugins
import { requestVantageApi, RequestMethod } from '../../../plugins/request';

// Types
import { User } from '../../../types/global';

// View
import { LanguageModuleProps } from './LanguageModule.types';
import {
  ActionsContainer,
  Container,
  Content,
  TitleContainer,
} from './LanguageModule.style';
import { showToast, ToastType } from '../../../layout/elements/Toast';

function LanguageModule({
  className,
  testId,
}: LanguageModuleProps): JSX.Element {
  // Dependencies
  const { t, i18n } = useTranslation();

  // Detect browser language only on first visit (when language is null)
  const getInitialLanguage = (): string => {
    let selectedLanguage: string = fallbackLanguage;
    const storedLanguage: string | null = getLanguage();

    if (storedLanguage) {
      selectedLanguage = storedLanguage;
    } else {
      // Detect browser language for first visit
      const browserLang: string = navigator.language || navigator.languages?.[0] || 'en';
      const normalizedLang: string = browserLang.toLowerCase().replace('-', '_');

      if (normalizedLang.startsWith('pt')) {
        selectedLanguage = 'pt_BR';
      }
    }

    return selectedLanguage;
  };

  const selectedLanguage: string = getInitialLanguage();

  /* Handles the change of the language */
  const handleLanguageChange = async (value: string): Promise<void> => {
    try {
      // Update local state immediately for better UX
      setLanguage(value);
      i18n.changeLanguage(value);

      // Send update to backend if user is authenticated
      const user: User | null = getUser();
      if (user) {
        await requestVantageApi.users({
          method: RequestMethod.PUT,
          path: `/user/${user.id}`,
          body: {
            language: value,
          },
        });
      }

      showToast({
        text: t('view.settings.languageModule.toastDescription'),
        type: ToastType.success,
      });
    } catch (error: unknown) {
      // Revert language change on error
      const currentLanguage: string | null = getLanguage();
      if (currentLanguage) {
        setLanguage(currentLanguage);
        i18n.changeLanguage(currentLanguage);
      }

      showToast({
        text: t('view.settings.languageModule.toastError'),
        type: ToastType.error,
      });
    }
  };

  return (
    <Container
      className={className}
      testId={testId}
      xs={24}
      lg={12}
    >
      <Content>
        <TitleContainer>
          <Paragraph
            weight={ParagraphWeight.bold}
          >
            {t('view.settings.languageModule.title')}
          </Paragraph>
        </TitleContainer>
        <ActionsContainer>
          <Select
            testId="language-select-language-module"
            label={t('common.language')}
            placeholder={t('common.language')}
            size={SelectSizeType.lg}
            items={[
              { text: t('common.languages.english'), id: Language.en_US },
              { text: t('common.languages.portuguese'), id: Language.pt_BR },
            ]}
            value={selectedLanguage}
            onChange={async (value): Promise<void> => handleLanguageChange(value as string)}
            allowClear={false}
          />
        </ActionsContainer>
      </Content>
    </Container>
  );
}

export { LanguageModule };
